import {Injectable} from "@angular/core";
import {JwtHelperService} from "@auth0/angular-jwt";
import {environment} from "../environments/environment";
import {UserInfo} from "../models/user-info";
import {DateTime} from 'luxon';
import {IdentityClient, LoginResponseDto, RefreshTokenRequestDto} from '../api/api-client';


@Injectable({
  providedIn: 'root',
})
export class AuthService {
  private jwtHelper = new JwtHelperService();
  public isLoggedIn!: boolean;
  private worker: Worker | null = null;
  private isRefreshing = false;

  // for all getters and setters: We are using the localstorage to save / access the token issued from the backend

  get AccessToken(): string {
    return <string>(localStorage.getItem(environment.AUTH_ACCESS_TOKEN_KEY) ?? "");
  }

  set AccessToken(val: string) {
    window.localStorage.removeItem(environment.AUTH_ACCESS_TOKEN_KEY);
    window.localStorage.setItem(environment.AUTH_ACCESS_TOKEN_KEY, val);
  }

  get RefreshToken(): string {
    return <string>(localStorage.getItem(environment.AUTH_REFRESH_TOKEN_KEY) ?? "");
  }

  set RefreshToken(val: string) {
    window.localStorage.removeItem(environment.AUTH_REFRESH_TOKEN_KEY);
    window.localStorage.setItem(environment.AUTH_REFRESH_TOKEN_KEY, val);
  }

  constructor(private identityService: IdentityClient,) {
    if (typeof Worker !== 'undefined') {
      this.worker = new Worker(new URL('./timer.worker', import.meta.url));
      this.worker.onmessage = ({ data }) => {
        if (data === 'tick') {
          this.refreshTokenIfNeeded();
        }
      };
    } else {
      // Fallback for environments without Web Worker support
      console.warn('Web Workers are not supported in this environment.');
      setInterval(() => {
        this.refreshTokenIfNeeded();
      }, 10000);
    }

    var token = this.jwtHelper.decodeToken(window.localStorage.getItem(environment.AUTH_ACCESS_TOKEN_KEY) ?? "");
    if (token) {
      let token = this.jwtHelper.decodeToken(window.localStorage.getItem(environment.AUTH_ACCESS_TOKEN_KEY) ?? "")
      let timeToRefresh = DateTime.fromSeconds(token.exp).minus({seconds: 20});
      if (DateTime.now() < timeToRefresh.plus({seconds: 20})) {
        this.isLoggedIn = true;
        this.startRefreshLogic();
      } else {
        this.isLoggedIn = false;
      }
    }

    document.addEventListener('visibilitychange', () => {
      if (document.visibilityState === 'visible') {
        this.refreshTokenIfNeeded();
      }
    });
  }

  startRefreshLogic() {
    if (this.worker) {
      this.worker.postMessage('start');
    }
  }

  signOut(): void {
    window.localStorage.clear();
    if (this.worker) {
      this.worker.terminate();
      this.worker = new Worker(new URL('./timer.worker', import.meta.url)); // Reset worker
      this.worker.onmessage = ({ data }) => {
        if (data === 'tick') {
          this.refreshTokenIfNeeded();
        }
      };
    }
    this.isLoggedIn = false;
  }


  public refreshTokenIfNeeded() {
    if (this.isRefreshing) return;

    // console.log("Checking if token refresh is needed ...") // Reduced log noise
    let token = this.jwtHelper.decodeToken(window.localStorage.getItem(environment.AUTH_ACCESS_TOKEN_KEY) ?? "")
    if (!token) return;

    let expirationTime = DateTime.fromSeconds(token.exp);
    let timeToRefresh = expirationTime.minus({seconds: 30});

    if (DateTime.now() > timeToRefresh) {
      console.log("Refreshing access token");
      this.isRefreshing = true;
      this.identityService.refresh(<RefreshTokenRequestDto>{refreshToken: this.RefreshToken}).subscribe({
        next: (x) => {
          this.saveToken(x.result);
          this.isRefreshing = false;
        },
        error: (err) => {
          console.error("Refresh failed", err);
          this.isRefreshing = false;
          this.signOut();
        }
      });
    }
  }

  public saveToken(tokens: LoginResponseDto): void {
    const decodedToken = this.jwtHelper.decodeToken(tokens.accessToken ?? "");
    this.AccessToken = tokens.accessToken ?? "";
    this.RefreshToken = tokens.refreshToken ?? "";
    let firstname = JSON.parse(decodedToken.given_name).FirstName;
    let lastname = JSON.parse(decodedToken.given_name).LastName;
    this.saveUser({FirstName: firstname, LastName: lastname, Language: decodedToken.language})
  }

  public getToken(): string {
    return localStorage.getItem(environment.AUTH_ACCESS_TOKEN_KEY) ?? "";
  }

  public saveUser(user: UserInfo): void {
    window.localStorage.removeItem(environment.AUTH_USER_KEY);
    window.localStorage.setItem(environment.AUTH_USER_KEY, JSON.stringify(user));
    this.isLoggedIn = true;
    this.startRefreshLogic();
  }

  public getUser(): UserInfo {
    return JSON.parse(localStorage.getItem(environment.AUTH_USER_KEY) ?? "");
  }
}
